/* eslint-disable */
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { mockUsers } from '../../lib/stories-data'

export function StoriesViewer({
    user,
    isOpen,
    onClose,
    onNextUser,
    onPrevUser,
}) {
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0)
    const [progress, setProgress] = useState(0)
    const [isPaused, setIsPaused] = useState(false)
    const isPressingRef = useRef(false)

    const currentStory = user.stories[currentStoryIndex]
    const storyDuration = 5000 // 5 seconds per story

    // Reset when user changes
    useEffect(() => {
        setCurrentStoryIndex(0)
        setProgress(0)
    }, [user.id])

    // Progress bar animation
    useEffect(() => {
        if (!isOpen || !currentStory || isPaused) return

        const startTime = Date.now()
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime
            const newProgress = (elapsed / storyDuration) * 100

            if (newProgress >= 100) {
                // Move to next story
                if (currentStoryIndex < user.stories.length - 1) {
                    setCurrentStoryIndex(currentStoryIndex + 1)
                    setProgress(0)
                } else {
                    // Last story of current user - move to next user or close
                    if (onNextUser) {
                        onNextUser()
                    } else {
                        // No more users, close the viewer
                        onClose()
                    }
                }
            } else {
                setProgress(newProgress)
            }
        }, 50)

        return () => clearInterval(interval)
    }, [isOpen, currentStoryIndex, user.stories.length, storyDuration, onClose, onNextUser, isPaused])

    const handleNext = () => {
        if (currentStoryIndex < user.stories.length - 1) {
            setCurrentStoryIndex(currentStoryIndex + 1)
            setProgress(0)
            setIsPaused(false)
        } else if (onNextUser) {
            onNextUser()
        } else {
            // No more users, close the viewer
            onClose()
        }
    }

    const handlePrev = () => {
        if (currentStoryIndex > 0) {
            setCurrentStoryIndex(currentStoryIndex - 1)
            setProgress(0)
            setIsPaused(false)
        } else if (onPrevUser) {
            onPrevUser()
        }
    }

    const handleMouseDown = () => {
        isPressingRef.current = true
        setIsPaused(true)
    }

    const handleMouseUp = () => {
        if (isPressingRef.current) {
            isPressingRef.current = false
            setIsPaused(false)
        }
    }

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp)
        window.addEventListener('touchend', handleMouseUp)
        return () => {
            window.removeEventListener('mouseup', handleMouseUp)
            window.removeEventListener('touchend', handleMouseUp)
        }
    }, [])

    // Hide navbar when stories are open
    useEffect(() => {
        if (isOpen) {
            // Hide navbar by targeting the nav element with z-[1000]
            const navbar = document.querySelector('nav[class*="z-[1000]"]')
            if (navbar) {
                navbar.style.display = 'none'
            }
            return () => {
                if (navbar) {
                    navbar.style.display = ''
                }
            }
        }
    }, [isOpen])

    if (!isOpen || !currentStory) return null

    return (
        <div className="fixed inset-0 z-[1000000] bg-black" style={{ isolation: 'isolate' }}>
            {/* Blurred background image - full screen on desktop */}
            <div
                className="absolute inset-0 w-full h-full md:block z-0"
                style={{
                    backgroundImage: `url(${currentStory?.imageUrl || "/placeholder.svg"})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'blur(20px)',
                    opacity: 0.6,
                }}
            />

            {/* Progress bars */}
            <div className="absolute top-0 left-0 right-0 z-[100] flex gap-1 p-2 md:p-3">
                {user.stories.map((_, index) => (
                    <div
                        key={index}
                        className="h-1 md:h-1.5 flex-1 bg-white/30 rounded-full overflow-hidden"
                    >
                        <div
                            className="h-full bg-white transition-all duration-100 ease-linear"
                            style={{
                                width:
                                    index < currentStoryIndex
                                        ? '100%'
                                        : index === currentStoryIndex
                                            ? `${progress}%`
                                            : '0%',
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* User info */}
            {/* <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                <img
                    src={user.avatar || "/placeholder.svg"}
                    alt={user.name}
                    className="w-10 h-10 rounded-full border-2 border-white"
                />
                <div className="text-white">
                    <p className="font-semibold text-sm">{user.name}</p>
                    <p className="text-xs opacity-75">
                        {new Date(currentStory.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                </div>
            </div> */}

            {/* Close button */}
            <button
                onClick={(e) => {
                    e.stopPropagation()
                    onClose()
                }}
                className="absolute top-4 right-4 z-[100] p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                aria-label="Close stories"
            >
                <X className="w-6 h-6 text-white" />
            </button>

            {/* Story image container with click zones */}
            <div className="relative w-full h-full flex items-center justify-center">
                {/* Story image - natural size on desktop, full screen on mobile */}
                <img
                    src={currentStory.imageUrl || "/placeholder.svg"}
                    alt="Story"
                    className="relative z-10 w-full h-full md:w-auto md:h-auto md:max-w-[90vw] md:max-h-[90vh] object-cover md:object-contain pointer-events-none"
                />

                {/* Left zone - previous story */}
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        handlePrev()
                    }}
                    className="absolute left-0 top-0 h-full w-1/2 md:w-1/3 z-50 cursor-pointer"
                    aria-label="Previous story"
                />

                {/* Center zone - pause on hold */}
                <div
                    onMouseDown={(e) => {
                        e.stopPropagation()
                        handleMouseDown()
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation()
                        handleMouseDown()
                    }}
                    className="absolute left-1/2 top-0 h-full w-1/2 md:w-1/3 -translate-x-1/2 z-50 cursor-pointer"
                />

                {/* Right zone - next story */}
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        handleNext()
                    }}
                    className="absolute right-0 top-0 h-full w-1/2 md:w-1/3 z-50 cursor-pointer"
                    aria-label="Next story"
                />
            </div>

            {/* Pause indicator */}
            {/* {isPaused && (
                <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                        <div className="w-12 h-12 flex items-center justify-center">
                            <div className="flex gap-1">
                                <div className="w-1 h-8 bg-white rounded-full" />
                                <div className="w-1 h-8 bg-white rounded-full" />
                            </div>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Story counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[100] text-white text-sm font-medium">
                {currentStoryIndex + 1} / {user.stories.length}
            </div>
        </div>
    )
}
